// src/pages/auth/ResetPassword.js
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Input, Button } from "../../components/common";
import "./ResetPassword.css";

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");

  useEffect(() => {
    if (location.state?.phoneNumber) {
      setPhoneNumber(location.state.phoneNumber);
    }

    // Verify that user came through OTP verification
    const isVerified = sessionStorage.getItem("resetPhoneVerified");
    if (!isVerified) {
      navigate("/forgot-password");
    }
  }, [location, navigate]);

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setError("");
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    setError("");
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validation
    if (!password.trim() || !confirmPassword.trim()) {
      setError("Please enter both password fields");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      setIsLoading(true);

      // Extract 10-digit mobile number from phoneNumber (e.g., "+919876543210" -> "9876543210")
      const mobile = Number(phoneNumber.replace(/\D/g, "").slice(-10));

      console.log("🔥 Calling backend to reset password for mobile:", mobile);

      // Call backend API to update password in MongoDB
      const backendResponse = await fetch("https://viz-sm-be.onrender.com/api/account/resetPassword", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mobile: mobile,
          newPassword: password,
        }),
      });

      const backendData = backendResponse.ok ? {} : await backendResponse.json();

      console.log("🔥 Backend response:", backendResponse.status, backendData);

      if (backendResponse.ok) {
        // ✅ Success
        setSuccess("Password reset successfully! Redirecting to login...");

        // Clear sessionStorage
        sessionStorage.removeItem("resetPhoneVerified");
        sessionStorage.removeItem("resetPhoneNumber");
        sessionStorage.removeItem("firebase_verificationId");

        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        setError(
          `Backend error: ${backendData.message || backendData.error || "Failed to reset password"}`
        );
      }
    } catch (err) {
      console.error("Password reset error:", err);
      setError("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = (e) => {
    e.preventDefault();
    navigate("/login");
  };

  return (
    <div className="reset-password-root">
      {/* TOP SECTION */}
      <div className="reset-password-top">
        <div className="reset-password-icon">
          <i className="fas fa-lock"></i>
        </div>
        <h1 className="reset-password-title">
          Set New <br /> Password
        </h1>
        <p className="reset-password-subtitle">
          Enter your new password to <br /> complete the reset process
        </p>
      </div>

      {/* BOTTOM SECTION */}
      <div className="reset-password-bottom">
        <form className="reset-password-form" onSubmit={handleResetPassword}>
          {/* Error Message */}
          {error && <p className="reset-password-error">{error}</p>}

          {/* Success Message */}
          {success && <p className="reset-password-success">{success}</p>}

          {/* Password Label */}
          <label className="reset-password-label">New Password</label>

          {/* Password Input */}
          <Input
            type="password"
            placeholder="Enter Password"
            className="reset-password-input"
            value={password}
            onChange={handlePasswordChange}
            required
          />

          {/* Confirm Password Label */}
          <label className="reset-password-label" style={{ marginTop: "1rem" }}>
            Confirm Password
          </label>

          {/* Confirm Password Input */}
          <Input
            type="password"
            placeholder="Enter Password again"
            className="reset-password-input"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            required
          />

          {/* Set New Password Button */}
          <Button
            type="submit"
            className="reset-password-button"
            disabled={isLoading}
            style={{
              background: "linear-gradient(90deg, #89D957 0%, #C9E265 100%)",
              color: "#062540",
              fontWeight: 600,
              fontSize: "1.05rem",
              padding: "0.9rem 2rem",
              borderRadius: "1.8rem",
              border: "none",
              marginTop: "1.5rem",
              minHeight: "2.8rem",
              width: "100%",
              cursor: isLoading ? "not-allowed" : "pointer",
            }}
          >
            {isLoading ? "Resetting..." : "Set New Password"}
          </Button>

          {/* Back to Login Link */}
          <a
            href="#"
            className="reset-password-back-link"
            onClick={handleBackToLogin}
            style={{ marginTop: "1rem", display: "block", textAlign: "center" }}
          >
            Back to Login
          </a>
        </form>
      </div>
    </div>
  );
}
